// askSmart-ui/src/pages/MapleCoin/operations/opsApi.ts
//
// The MapleCoin_Operations API (/maplecoin/ops). The server allows it only to
// a platform Super-Admin whose active organization is MapleCoin_Operations;
// this client adds nothing to that decision. The signed-in session is the
// only identity sent — the maker/checker actor is taken from the token.
// Amounts are exact minor-unit strings, never numbers.
import { Currency, MinorString, TransactionDirection, TransactionStatus } from '../types';

export class OpsApiError extends Error {
  status: number;
  code: string | null;
  detail: string | null;
  negativeBalances: { accountCode: string; currentBalanceMinor: string; resultingBalanceMinor: string }[];
  constructor(status: number, body: any) {
    super(`MapleCoin Operations request failed (${status})`);
    this.name = 'OpsApiError';
    this.status = status;
    this.code = typeof body?.error === 'string' ? body.error : null;
    this.detail = typeof body?.message === 'string' ? body.message : null;
    this.negativeBalances = Array.isArray(body?.negativeBalances) ? body.negativeBalances : [];
  }
}

const url = (path: string) => `${process.env.REACT_APP_BACKEND_URL}/maplecoin/ops${path}`;

// Every response may carry display names for the employee ids it contains;
// the console shell collects them (see MapleCoinOperations.tsx).
type PeopleListener = (people: Record<string, { name: string | null; email: string | null }>) => void;
const peopleListeners = new Set<PeopleListener>();
export function onPeople(fn: PeopleListener): () => void {
  peopleListeners.add(fn);
  return () => { peopleListeners.delete(fn); };
}

async function read<T>(res: Response): Promise<T> {
  if (!res.ok) throw new OpsApiError(res.status, await res.json().catch(() => null));
  const body = await res.json();
  if (body && typeof body === "object" && body.people && typeof body.people === "object" && Object.keys(body.people).length) {
    peopleListeners.forEach((fn) => fn(body.people));
  }
  return body as T;
}

async function get<T>(path: string): Promise<T> {
  return read<T>(await fetch(url(path), { credentials: 'include' }));
}

// Writes carry the console's intent header; the server refuses them without it.
async function post<T>(path: string, body: unknown): Promise<T> {
  return read<T>(await fetch(url(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'MapleCoin-Operations' },
    body: JSON.stringify(body),
  }));
}

const query = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, v); });
  const s = q.toString();
  return s ? `?${s}` : '';
};

// ── Shapes ──────────────────────────────────────────────────────────────────

export interface Actor { sub: string; orgId: string }

/** Display names for the Auth0 ids in a response (the id stays the identity). */
export type People = Record<string, { name: string | null; email: string | null }>;
export interface WithPeople { people?: People }

export interface IntegrityCheck {
  checkSeq: string;
  checkedAt: string;
  requestedBy: Actor;
  status: 'VERIFIED' | 'PROBLEMS_FOUND';
  sealsChecked: string;
  lastSealSeq: string | null;
  lastJournalSeq: string | null;
  sealProblemCount: number;
  unsealedJournalCount: number;
  projectionMismatchCount: number;
  controlPostingCount: number;
  unbalancedCurrencyCount: number;
  problems: { kind: string; problem: string; sealSeq?: string; journalSeq?: string | null }[];
  durationMs: number;
}

export interface IntegrityStatus {
  latestCheck: IntegrityCheck | null;
  currentSealSeq: string | null;
  currentJournalSeq: string | null;
  sealsSinceCheck: string;
}

export interface CurrencyPosition {
  currency: Currency;
  customerAccounts: number;
  availableMinor: MinorString;
  pendingMinor: MinorString;
  totalCustomerFundsMinor: MinorString;
  sandboxFundingMinor: MinorString | null;
  internalBalance: { customerLiabilitiesMinor: MinorString; sandboxAssetMinor: MinorString; differenceMinor: MinorString };
}

export interface Dashboard {
  environment: 'development' | 'staging' | 'production';
  asOf: string;
  lastJournalSeq: string | null;
  currencies: CurrencyPosition[];
  transactions: { total: number; created: number; pending: number; completed: number; failed: number; reversed: number; reversals: number };
  interventions: { pendingApproval: number };
  integrity: IntegrityStatus;
}

export interface LedgerAccount {
  code: string;
  name: string;
  accountType: string;
  normalSide: 'DEBIT' | 'CREDIT';
  currency: Currency;
  isControl: boolean;
  status: string;
  environmentScope: 'ALL' | 'NON_PRODUCTION';
  allowNegative: boolean;
  ownBalanceMinor: MinorString;
  childAccounts: number;
  childrenBalanceMinor: MinorString;
  balanceMinor: MinorString;
}

export interface Subaccount {
  ledgerCode: string;
  customerNumber: string;
  accountNumber: string;
  currency: Currency;
  accountStatus: 'OPEN' | 'FROZEN' | 'CLOSED';
  balanceMinor: MinorString;
  availableMinor: MinorString;
  pendingMinor: MinorString;
}

export interface SubaccountPage {
  controlCode: string;
  name: string;
  currency: Currency;
  childAccounts: number;
  childrenBalanceMinor: MinorString;
  subaccounts: Subaccount[];
  nextAfter: string | null;
}

export interface OpsTransaction {
  reference: string;
  customerNumber: string;
  accountNumber: string;
  type: string;
  rail: string;
  direction: TransactionDirection;
  status: TransactionStatus;
  amountMinor: MinorString;
  currency: Currency;
  createdAt: string;
  statusChangedAt: string;
  reversesReference: string | null;
  reversedByReference: string | null;
}

export interface JournalEntry { accountCode: string; accountName: string; side: 'DEBIT' | 'CREDIT'; amountMinor: MinorString; currency: Currency }

export interface Journal {
  journalSeq: string;
  journalId: string;
  journalType: string;
  effectiveDate: string;
  postedAt: string;
  actorType: string;
  actorId: string;
  reason: string | null;
  reference: string | null;
  transactionReference: string | null;
  purpose: string | null;
  reversesJournalSeq: string | null;
  reversedByJournalSeq: string | null;
  sealSeq: string | null;
  sealedAt: string | null;
  entries: JournalEntry[];
}

export interface JournalDetail extends Journal {
  auditEvents: { action: string; actorType: string; actorId: string; requestId: string | null; occurredAt: string; context: Record<string, unknown> | null }[];
  seal: { sealSeq: string; sealedAt: string; contentHash: string; prevSealHash: string; sealHash: string } | null;
}

export type InterventionStatus = 'PENDING_APPROVAL' | 'REJECTED' | 'EXECUTED' | 'EXECUTION_REFUSED';

export type ControlReasonCode = 'FRAUD_REVIEW' | 'COMPLIANCE_REVIEW' | 'OPERATIONAL';
export type RestrictionType = 'NO_OUTGOING' | 'NO_INCOMING';
export type ControlStatus = 'ACTIVE' | 'RELEASED' | 'EXPIRED';

interface InterventionBase {
  reference: string;
  status: InterventionStatus;
  reason: string;
  requestedBy: Actor;
  requestedAt: string;
  requestedByYou: boolean;
  decidedByYou: boolean;
  decision: { decision: 'APPROVED' | 'REJECTED'; decidedBy: Actor; reason: string | null; decidedAt: string } | null;
  execution: { outcome: 'EXECUTED' | 'REFUSED'; executedAt: string; resultTransactionReference: string | null;
    refusalCode: string | null; refusalMessage: string | null } | null;
  alreadyApplied?: boolean;
}

/** A maker/checker request: a reversal (Stage 9), or releasing a hold or restriction (C1). */
export type Intervention = InterventionBase & (
  | { type: 'TRANSACTION_REVERSAL';
      target: { transactionReference: string; type: string; direction: TransactionDirection; status: TransactionStatus;
        amountMinor: MinorString; currency: Currency; accountNumber: string; customerNumber: string } }
  | { type: 'HOLD_RELEASE';
      target: { kind: 'HOLD'; holdReference: string; amountMinor: MinorString; currency: Currency; reasonCode: ControlReasonCode;
        holdStatus: ControlStatus; accountNumber: string; customerNumber: string } }
  | { type: 'RESTRICTION_RELEASE';
      target: { kind: 'RESTRICTION'; restrictionReference: string; restrictionType: RestrictionType; reasonCode: ControlReasonCode;
        restrictionStatus: ControlStatus; accountNumber: string; customerNumber: string } });

// ── Holds and restrictions (C1) ─────────────────────────────────────────────

interface ControlBase {
  reference: string;
  accountNumber: string;
  customerNumber: string;
  reasonCode: ControlReasonCode;
  reason?: string;
  status: ControlStatus;
  placedAt: string;
  placedBy: { type: 'OPERATOR' | 'SYSTEM' | 'INTEGRATION'; id: string; orgId: string | null };
  externalSource: string | null;
  externalReference: string | null;
  release: { releasedAt: string; releasedBy: { type: string; id: string }; interventionReference: string | null; reason?: string } | null;
  alreadyApplied?: boolean;
}
export interface Hold extends ControlBase { currency: Currency; amountMinor: MinorString; expiresAt: string | null }
export interface Restriction extends ControlBase { restrictionType: RestrictionType }

export interface AccountBalances {
  accountNumber: string;
  customerNumber: string;
  currency: Currency;
  status: 'OPEN' | 'FROZEN' | 'CLOSED';
  openedAt: string;
  /** The cleared (ledger) balance, holds not deducted. */
  availableMinor: MinorString;
  heldMinor: MinorString;
  spendableMinor: MinorString;
  pendingMinor: MinorString;
  restrictions: RestrictionType[];
}

export interface AccountControls {
  account: AccountBalances;
  holds: Hold[];
  restrictions: Restriction[];
  releaseRequests: Intervention[];
}

export interface ActiveControls {
  activeHolds: number;
  heldByCurrency: Partial<Record<Currency, MinorString>>;
  activeRestrictions: number;
  holds: Hold[];
  restrictions: Restriction[];
}

export interface JobRun {
  runId: string; runSeq: string; jobName: string; occurrenceAt: string; attempt: number;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'ABANDONED';
  workerId: string; claimedAt: string; finishedAt: string | null; errorCode: string | null; errorMessage: string | null;
}
export interface Jobs {
  definitions: { name: string; description: string; scheduleKind: 'INTERVAL' | 'DAILY'; intervalSeconds: number | null;
    dailyTimes: string[] | null; timeZone: string | null; onAbandon: 'RETRY' | 'REVIEW' }[];
  recentRuns: JobRun[];
  needsReview: JobRun[];
}

export interface OpsTransactionDetail extends OpsTransaction {
  externalSource: string;
  externalReference: string;
  reason: string | null;
  initiatedBy: { type: string; id: string };
  events: { fromStatus: TransactionStatus | null; toStatus: TransactionStatus; action: string; actorType: string; actorId: string;
    reason: string | null; journalSeq: string | null; occurredAt: string }[];
  postings: Journal[];
  interventions: Intervention[];
  reversal: { eligible: boolean; refusalCode: string | null; message: string | null };
}

export interface AccessEvent {
  eventSeq: string;
  occurredAt: string;
  actorSub: string;
  actorOrgId: string | null;
  action: 'CUSTOMER_VIEWED' | 'ACCOUNT_VIEWED' | 'ACCOUNT_ACTIVITY_VIEWED' | 'TRANSACTION_VIEWED' | 'JOURNAL_VIEWED' | 'INTERVENTION_VIEWED';
  /** ADMIN (customer servicing) or OPERATIONS. */
  channel: 'ADMIN' | 'OPERATIONS';
  customerNumber: string;
  accountNumber: string | null;
  transactionReference: string | null;
  requestId: string | null;
}

// ── Calls ───────────────────────────────────────────────────────────────────

export const getDashboard = () => get<Dashboard & WithPeople>('/dashboard');
export const getAccounts = () => get<{ accounts: LedgerAccount[] }>('/accounts');
export const getSubaccounts = (code: string, after?: string) =>
  get<SubaccountPage>(`/accounts/${encodeURIComponent(code)}/subaccounts${query({ limit: '100', after })}`);

export type TransactionFilter = Partial<Record<'from' | 'to' | 'reference' | 'customerNumber' | 'accountNumber' | 'type'
  | 'status' | 'direction' | 'minAmountMinor' | 'maxAmountMinor' | 'before' | 'limit', string>>;
export const searchTransactions = (f: TransactionFilter) =>
  get<{ types: string[]; transactions: OpsTransaction[]; nextBefore: string | null }>(`/transactions${query(f)}`);
export const getTransaction = (reference: string) => get<OpsTransactionDetail & WithPeople>(`/transactions/${encodeURIComponent(reference)}`);

export type JournalFilter = Partial<Record<'from' | 'to' | 'journalSeq' | 'journalType' | 'transactionReference'
  | 'accountCode' | 'before' | 'limit', string>>;
export const listJournals = (f: JournalFilter) =>
  get<{ journalTypes: string[]; journals: Journal[]; nextBefore: string | null }>(`/journals${query(f)}`);
export const getJournal = (journalSeq: string) => get<JournalDetail & WithPeople>(`/journals/${encodeURIComponent(journalSeq)}`);

export const getIntegrity = () => get<IntegrityStatus & WithPeople>('/integrity');
export const runIntegrityCheck = () => post<IntegrityCheck>('/integrity/checks', {});

export type AccessFilter = Partial<Record<'from' | 'to' | 'actorSub' | 'customerNumber' | 'accountNumber' | 'action' | 'channel' | 'before' | 'limit', string>>;
export const listAccessEvents = (f: AccessFilter) =>
  get<{ actions: string[]; channels?: string[]; admins: { sub: string; events: number; lastAt: string }[]; events: AccessEvent[];
    nextBefore: string | null } & WithPeople>(
    `/access-events${query(f)}`);

export const listInterventions = (status?: InterventionStatus) =>
  get<{ counts: Record<InterventionStatus, number>; interventions: Intervention[] } & WithPeople>(`/interventions${query({ status })}`);
export const requestReversal = (transactionReference: string, reason: string, idempotencyKey: string) =>
  post<Intervention>('/interventions/reversals', { transactionReference, reason, idempotencyKey });
export const decideIntervention = (reference: string, decision: 'APPROVE' | 'REJECT', reason?: string) =>
  post<Intervention>(`/interventions/${encodeURIComponent(reference)}/decision`, { decision, ...(reason ? { reason } : {}) });

// ── Holds and restrictions (C1): placing is one user; releasing is maker/checker ──

export const getActiveControls = () => get<ActiveControls & WithPeople>('/controls');
export const getAccountControls = (accountNumber: string) =>
  get<AccountControls & WithPeople>(`/account-controls/${encodeURIComponent(accountNumber)}`);
export const placeHold = (h: { accountNumber: string; amountMinor: MinorString; currency: Currency; reasonCode: ControlReasonCode;
  reason: string; expiresAt?: string; idempotencyKey: string }) => post<Hold>('/holds', h);
export const placeRestriction = (r: { accountNumber: string; restrictionType: RestrictionType; reasonCode: ControlReasonCode;
  reason: string; idempotencyKey: string }) => post<Restriction>('/restrictions', r);
export const requestHoldRelease = (holdReference: string, reason: string, idempotencyKey: string) =>
  post<Intervention>('/interventions/hold-releases', { holdReference, reason, idempotencyKey });
export const requestRestrictionRelease = (restrictionReference: string, reason: string, idempotencyKey: string) =>
  post<Intervention>('/interventions/restriction-releases', { restrictionReference, reason, idempotencyKey });

export const getJobs = () => get<Jobs>('/jobs');

// ── External banking (Stage 10) ─────────────────────────────────────────────
// Provider configuration and connection state (never secret values),
// external events with their handling outcomes, and reconciliation.

export interface ServiceConnection { attempted: boolean; lastSuccessAt: string | null; lastFailureAt: string | null; lastFailure: string | null }

export interface ExternalBanking {
  providers: { provider: string; name: string; environment: 'STAGING' | 'PRODUCTION' }[];
  policies: { provider: string; rail: string; eventType: string; action: 'RECORD_ONLY' | 'HOLD_FOR_REVIEW'; description: string }[];
  events: { total: number; byStatus: Record<string, number>; lastReceivedAt: string | null };
  linkedCustomers: number;
  linkedAccounts: number;
  reconciliation: { batches: number; lastReceivedAt: string | null };
  ptc: {
    environment: 'STAGING' | null;
    configured: boolean;
    problem: string | null;
    services: Record<'EFT' | 'INTERAC', { configured: boolean; keyIdConfigured: boolean }>;
    hostedInteracEndpointsEnabled: boolean;
    eftRejectionWebhookEnabled: boolean;
    connection: Record<'EFT' | 'INTERAC', ServiceConnection>;
    lastConnectionTest?: PtcConnectionTest | null;
  };
}

/** The Operations "Test connection" result — outcomes and timings only, never a token. */
export interface PtcConnectionTest {
  ranAt: string;
  service: 'EFT';
  environment: 'STAGING';
  outcome: 'SUCCEEDED' | 'AUTH_FAILED' | 'BALANCE_FAILED' | 'NOT_CONFIGURED' | 'REFUSED';
  message: string;
  signer: { kind: 'KEY_VAULT'; vault: string; keyName: string; keyVersion: string } | { kind: 'LOCAL_PEM' } | null;
  keyIdSent: boolean;
  token: { ok: boolean; newTokenRequested: boolean; tokenRequestsThisProcess: number; lifetimeSeconds: number | null;
    issuedAt: string | null; refreshAt: string | null; error?: string; httpStatus?: number; oauthError?: string } | null;
  balance: { ok: boolean; availableBalance?: string; accountBalance?: string; httpStatus?: number; errorCode?: string; error?: string } | null;
}

export const testPtcConnection = () => post<PtcConnectionTest>('/external-banking/ptc/connection-test', {});

export interface ExternalEvent {
  eventSeq: string;
  provider: string;
  environment: string;
  rail: string;
  channel: string;
  eventType: string;
  externalEventKey: string;
  externalReference: string | null;
  externalAccountRef: string | null;
  externalStatus: string | null;
  direction: TransactionDirection | null;
  amountMinor: MinorString | null;
  currency: string | null;
  occurredAt: string | null;
  receivedAt: string;
  accountNumber: string | null;
  customerNumber: string | null;
  conflictsWithEventSeq: string | null;
  current: { status: string; reasonCode: string; detail: string | null; at: string } | null;
  outcomes: { outcome: string; reasonCode: string; detail: string | null; actorType: string; actorId: string; at: string }[];
}

export interface ReconciliationFinding {
  findingNo: number; kind: string; lineNo: number | null; externalReference: string | null;
  internalEventSeq: string | null; internalTransactionReference: string | null;
  expectedMinor: MinorString | null; actualMinor: MinorString | null; detail: string | null;
}
export interface ReconciliationRun {
  runSeq: string; ranAt: string; actorType: string; actorId: string; counts: Record<string, number>; findings: ReconciliationFinding[];
}
export interface ReconciliationBatch {
  batchSeq: string; provider: string; environment: string; rail: string; sourceType: string; sourceName: string | null;
  activityDate: string; settlementDate: string | null; currency: Currency; itemCount: number; receivedAt: string;
  declared: { creditCount: number | null; creditTotalMinor: string | null; debitCount: number | null; debitTotalMinor: string | null };
  latestRun: ReconciliationRun | null;
}

export const getExternalBanking = () => get<ExternalBanking>('/external-banking');
export const listExternalEvents = (f: Partial<Record<'status' | 'rail' | 'eventType' | 'before' | 'limit', string>>) =>
  get<{ events: ExternalEvent[]; nextBefore: string | null } & WithPeople>(`/external-events${query(f)}`);
export const closeExternalEvent = (eventSeq: string, reason: string) =>
  post<ExternalEvent & WithPeople>(`/external-events/${encodeURIComponent(eventSeq)}/close`, { reason });
export const listReconciliationBatches = () => get<{ batches: ReconciliationBatch[] }>('/reconciliation/batches');
export const runReconciliation = (batchSeq: string) =>
  post<ReconciliationRun>(`/reconciliation/batches/${encodeURIComponent(batchSeq)}/runs`, {});
